'use client';

import { useMemo, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

/**
 * Closes a modal only when the user presses and releases on the dimmed backdrop.
 * Avoids accidental close when the pointer goes down inside the panel and up outside
 * (e.g. text selection or dragging).
 */
export function useModalBackdropDismiss(onRequestClose: () => void) {
  const downOnBackdropRef = useRef(false);
  const onRequestCloseRef = useRef(onRequestClose);
  onRequestCloseRef.current = onRequestClose;

  const overlayProps = useMemo(
    () => ({
      onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) downOnBackdropRef.current = true;
      },
      onPointerUp: (e: ReactPointerEvent<HTMLDivElement>) => {
        if (downOnBackdropRef.current && e.target === e.currentTarget) {
          onRequestCloseRef.current();
        }
        downOnBackdropRef.current = false;
      },
    }),
    [],
  );

  const panelPointerDownCaptureProps = useMemo(
    () => ({
      onPointerDownCapture: () => {
        downOnBackdropRef.current = false;
      },
    }),
    [],
  );

  return { overlayProps, panelPointerDownCaptureProps };
}
